/**
 * Kazakhstan-only replacements for Russian copy.
 *
 * `ru.ts` is ONE catalog serving two deployments: uztender.com (ru + uz) and
 * kaztender.com (ru + kk). Brand, country name, currency word and the company-id
 * label already come from `countryConfig` as `{{tokens}}`. What tokens cannot
 * carry is a sentence that is only TRUE in Uzbekistan — «Карту обрабатывают
 * Payme, Click и Octobank», «разбор на узбекском и русском», «данные с
 * xarid.uzex.uz», «Telegram-алерты» on a deployment with no bot — and those were
 * rendered verbatim on kaztender.com (audit 2026-09-29).
 *
 * `i18n/index.ts` lays this map over `ru` when — and only when —
 * `countryConfig.code === 'kz'`. The Uzbek build never reads it, so no Uzbek
 * string can change by editing this file.
 *
 * RULES
 *  - A key here MUST exist in `ru.ts` and may use only the placeholders the
 *    `ru.ts` value uses (`kz-overrides.test.ts` enforces both), so a caller
 *    never has to know which deployment it renders on.
 *  - The Kazakh catalog (`kk.ts`) is Kazakhstan-only already; fix it in place.
 *  - Payment wording (owner, 2026-10-13): the provider on KZ will be Stripe, cards
 *    are Visa / Mastercard, nothing names Payme, Click, Octobank, Uzcard or Humo.
 *    Since 2026-10-14 the checkout itself is Stripe's too (`payments.checkout`
 *    in the country config): one button per plan that leaves for Stripe
 *    Checkout. The card-sheet strings below stay overridden only because the
 *    keys are shared; the KZ build never opens that sheet.
 *  - Alerts on KZ arrive by e-mail or in Telegram: KZ runs its own bot
 *    (@kaztender_com_bot) since 2026-10-14, and e-mail stays a full channel
 *    there (`messaging.emailFirstAlert`). Lines that promised e-mail only now
 *    name both; where `ru.ts` already says «в Telegram или на email» the
 *    override is gone.
 *  - The company id is «БИН/ИИН», the product classifier is «ЕНС ТРУ».
 */
export const ruKz: Record<string, string> = {
  // «(35+ источников)» в ru.ts — число источников Узбекистана; здесь их другое количество.
  'plans.feature.allTenders': 'Все тендеры Республики {{country}}',
  // The Pro card's «частные тендеры» line named Uzbek banks and telecom. On KZ
  // the corporate layer is Mitwork and Samruk-Kazyna (both harvested), so the
  // line names exactly those two (owner 2026-10-15; Samruk added 2026-10-18).
  'plans.feature.privateTenders':
    'Корпоративные закупки компаний на площадках Mitwork и Самрук-Казына',
  // IFI sources with live KZ lots on 2026-10-15: UNGM 43, ADB 2, IsDB 2,
  // UNDP 2, EBRD 1 — and World Bank 0, so it is not named.
  'plans.feature.ifi': 'Тендеры МФИ: ООН, ADB, ЕБРР',
  // ── What the catalogue is ───────────────────────────────────────────────
  'region.intro':
    'Государственные тендеры и закупки в {{region}}: сейчас {{count}} активных. По каждому доступны бюджет, срок подачи заявок и краткое описание на русском языке. Фильтруйте по категории, сохраняйте поиск и получайте новые тендеры на почту или в Telegram.',
  'category.intro':
    '«{{category}}» — одно из направлений государственных закупок в {{countryPrep}}. Сейчас в каталоге {{count}} актуальных тендеров этой категории: по каждому указаны начальная сумма, срок подачи заявок и краткое описание на русском языке. Уточните регион в каталоге, сохраните поиск и получайте новые закупки на почту или в Telegram.',
  'seo.catalog.description':
    'Каталог государственных тендеров и госзакупок {{countryGen}}: актуальные закупки, краткие описания на русском языке, фильтры по региону и категории, оповещения на почту и в Telegram — на {{brand}}.',
  'about.lead':
    '{{brand}} — это единый каталог государственных закупок {{countryGen}}. Мы собираем тендеры с официальных площадок в одном месте, добавляем разбор закупок на русском языке и помогаем поставщикам быстрее находить подходящие госконтракты.',
  // «каждого» overpromised (audit 2026-10-17): not every lot has a summary yet.
  'about.feature.ai.desc':
    'Краткое описание по большинству тендеров на русском языке — суть закупки понятна сразу.',
  'about.feature.alerts.title': 'Оповещения в Telegram и на почту',
  'about.sources.body':
    'Мы агрегируем публичные данные с официальных площадок госзакупок {{countryGen}} (goszakup.gov.kz, mitwork.kz, Самрук-Казына и другие). {{brand}} — независимый сервис и не является государственным порталом.',
  'ifi.page.intro':
    'Закупки по проектам, финансируемым международными институтами. Заказчик — государственное ведомство {{countryGen}}, финансирование банковское, поэтому бюджет публикуется не в {{currency}}, а в валюте проекта.',
  'result.hint.ifi':
    'Проекты, финансируемые международными институтами (Всемирный банк, АБР и др.). Бюджет в {{currency}} не публикуется, поэтому в счёт входят открытые нотисы без суммы.',
  // /ifi title and description named the World Bank and AIIB first — on
  // 2026-10-16 both had 0 live KZ lots (UNGM 42, ADB 2, IsDB 2, UNDP 2, EBRD 1).
  'ifi.seo.title': 'Тендеры международных финансовых институтов и агентств ООН в {{countryPrep}}',
  'ifi.seo.description':
    'Открытые закупки по проектам международных финансовых институтов в {{countryPrep}}: агентства ООН (UNGM, ПРООН), АБР, ИБР, ЕБРР. Сроки, документы, требования.',
  // /directory: ru.ts counts Uzbekistan's 31 categories and 14 regions. Kazakhstan
  // has the 88 ЕНС ТРУ sections (`kz-sec-NN`) and 20 regions (17 oblasts + 3
  // cities) — both fixed classifiers, not live counts.
  'directory.seoDesc':
    'Указатель всех разделов каталога госзакупок {{countryGen}}: тендеры по 88 направлениям закупки и по 20 регионам, с числом активных тендеров в каждом.',
  // «Кооперация» is the Uzbek cooperation.uz layer; KZ ingests no such source.
  // The section is not offered there — these only keep a stray deep link honest.
  'supply.page.intro':
    'Заявки на поставку без объявленного бюджета — они вынесены в отдельный раздел и не участвуют в основном каталоге.',
  'supply.seo.title': 'Запросы предложений без объявленного бюджета',
  'supply.seo.description': 'Заявки на поставку товаров и услуг без указания бюджета.',
  // ── Amount unit beside a number: «₸», not «тенге» ─────────────────────────
  // Owner, 2026-10-15: on a phone the lot's budget card read «250 тыс.» with
  // «тенге» wrapped onto the next line. The sign keeps every narrow block —
  // budget card, catalogue cards, market data, company pages — on one line.
  // Sentences that spell the currency out ({{currency}}, `plans.som`) keep the
  // word. Uzbekistan's «сум» is untouched (ru.ts).
  'tender.budget.unit': '₸',
  'tender.openOriginalCoop': 'Открыть оригинал на площадке',
  'saved.includeSupplyHint': 'Заявки без объявленного бюджета по этому фильтру.',
  'buyer.settlement.source': 'Источник: открытые данные о договорах',
  'tender.reveal.limitedDay':
    'Лимит открытия контактов на сегодня исчерпан ({{limit}}). Он снимется в {{time}} по времени Астаны. Если это ошибка — напишите в поддержку.',

  // ── Alerts: e-mail or Telegram (both channels since 2026-10-14) ─────────
  'search.empty.body':
    'Подходящего лота сейчас в каталоге нет. Подпишитесь — пришлём уведомление на почту или в Telegram в день публикации.',
  'tender.gate.guest':
    'Регистрируйтесь бесплатно — сохранённые поиски и оповещения на почту или в Telegram',
  'onboarding.niche.desc':
    'Мы будем присылать на почту или в Telegram каждый новый подходящий тендер по этой нише.',
  'onboarding.categories.desc':
    'Новые тендеры и оповещения на почту или в Telegram по выбранной нише.',
  'catalog.nudge.compact':
    'Выберите категорию и сохраните — новые тендеры придут на почту или в Telegram.',
  'tender.bidPackage.preparing': 'Готовим документ, пришлём на почту',
  'tender.bidPackage.sendHint':
    'Если файл не скачивается — встроенный браузер приложения так умеет, — заберите его почтой.',

  // ── /plans ──────────────────────────────────────────────────────────────
  // No supplier count: «более 2 700» is the Uzbek user table. Kazakhstan has no
  // number to back a claim yet, and the rule in ru.ts is «never write a number
  // the table does not back».
  'plans.social': 'Тендеры и закупки для поставщиков Республики {{country}}',
  // No price in the snippet: the Uzbek one («399 000 сум») rendered here as
  // «399 000 тенге». Real KZ prices come from the API onto the page itself.
  'plans.seoDesc':
    'Тарифы {{brand}}: Pro — контакты заказчиков, документы тендеров и экспорт данных по госзакупкам {{countryGen}}. Бесплатный доступ к описаниям и требованиям.',
  'plans.subtitle':
    'Доступ к контактам заказчиков, экспорт в Excel, оповещения о новых тендерах на почту и в Telegram.',
  'plans.feature.categoryNiche': '1 ниша + оповещения на почту или в Telegram',
  'plans.feature.savedSearchOne': 'До 3 сохранённых поисков + оповещения на почту или в Telegram',
  'plans.feature.categoryLimitUnlimited': 'Неограниченно категорий ЕНС ТРУ',
  // The marks Visa · Mastercard · Stripe follow this word on the page (`PaymentMarks`).
  'plans.paymentVia': 'Оплата картой',
  'plans.trust.secureText': 'Карту обрабатывает Stripe — мы не видим и не храним её номер',
  'plans.trust.receiptTitle': 'Квитанция об оплате',
  'plans.trust.receiptText': 'После оплаты квитанция приходит на вашу почту',
  'plans.payOnceLead': 'Оплата на стороне Stripe · карту вводить у нас не нужно',
  'plans.payWithPayme': 'Оплатить картой',
  'plans.payWithClick': 'Оплатить картой',
  'plans.payOnceViaPayme': 'Оплатить картой',
  'plans.payWithCard': 'Оплатить картой (Visa/Mastercard)',
  'plans.clickCards': 'Visa · Mastercard',
  'plans.paymeOnlyNote': 'Помесячная оплата — только онлайн, банковской картой.',
  'plans.octo.tabOcto': 'Stripe',
  'plans.octo.tabPayme': 'Банковская карта',
  'plans.octo.continue': 'Продолжить на Stripe',
  'plans.octo.redirectNote': 'Данные карты вводятся на защищённой странице Stripe',
  'plans.tinRequired':
    'Для этого тарифа нужен БИН/ИИН компании. Укажите его — и вернитесь к оплате.',
  // ── What is sold: a monthly Stripe subscription, nothing else (15.10) ──────
  // ru.ts offers a one-time payment, a year plan, payment by invoice and «другой
  // способ оплаты» in these lines; Kazakhstan has none of them.
  'plans.trust.noSurpriseText':
    'Помесячная подписка: напоминание перед каждым списанием, продление отключается в любой момент',
  'plans.bannerPair': 'Pro и Бизнес · помесячно',
  'requisites.note':
    'Платежи банковской картой за подписку проходят через платёжную систему Stripe.',
  'billing.return.failedBody':
    'Банк отклонил платёж или оплата была отменена — подписка не активирована. Можно попробовать ещё раз: той же картой или другой.',
  'phone.subtitle': 'Свяжемся по вопросам подписки. Звонков по рекламе не будет.',
  // ── The cabinet's subscription panel: the card and the renewals are Stripe's ──
  'billing.autoRenew.card': 'Карта в Stripe: {{pan}}',
  'billing.autoRenew.note':
    'Списание через Stripe в день окончания периода, без подтверждения. Отключить можно в любой момент.',
  'billing.autoRenew.retention':
    'Пока продление включено, доступ не прерывается. Выключить — один клик: подписка не продлится и закончится {{date}}.',
  // ── Card sheet ──────────────────────────────────────────────────────────
  'billing.card.subtitle': 'Данные карты обрабатывает Stripe. Мы карту не храним.',
  'billing.card.subscribeSubtitle': 'Карту мы не храним — данные обрабатывает Stripe.',
  'billing.card.receiptPayme': 'Квитанция об оплате придёт на почту',
  'billing.card.receiptOcto': 'Квитанция об оплате будет доступна в истории платежей',
  'billing.history.fiscalReceipt': 'Квитанция',
  'billing.card.nextHint': 'Дальше банк может запросить код подтверждения.',
  'billing.card.smsFromPayme': 'Код подтверждения — от вашего банка, номер карты мы не видим',
  'billing.card.securedByPay': 'Платёж защищён — обрабатывает Stripe',
  'billing.card.securedByOcto': 'Платёж защищён — обрабатывает Stripe',
  'billing.card.securedByClick': 'Платёж защищён — обрабатывает Stripe',
  'billing.card.securedByPayClick': 'Платёж защищён — обрабатывает Stripe',
  'billing.card.smsFrom':
    'Код подтверждения придёт от вашего банка (не от {{brand}}) на номер, привязанный к карте.',
  'billing.card.otpSubtitle': 'Введите код подтверждения от банка.',
  'billing.card.smsSentTo': 'Банк отправил код подтверждения на {{phone}}.',
  'billing.card.smsSentGeneric': 'Банк отправил код подтверждения на номер, привязанный к карте.',
  'billing.card.errorTinRequired':
    'Для этого тарифа нужен БИН/ИИН компании. Укажите его в настройках и повторите оплату — код подтверждения здесь ни при чём.',
  'billing.card.errorCorporateCard':
    'Эта корпоративная карта не принимается. Используйте другую карту.',
  'billing.card.errorCurrency': 'Валюта карты не подходит. Попробуйте другую карту.',
  'billing.card.errorUnsupported':
    'Такая карта не поддерживается. Проверьте номер: принимаются Visa и Mastercard.',
  'billing.card.errorUnsupportedOcto':
    'Эта карта не поддерживается. Используйте карту Visa или Mastercard.',
  'billing.card.payViaOcto': 'Оплатить другой картой',
  'billing.card.foreignBrandHint': 'Принимаются карты Visa и Mastercard.',
  'billing.card.corporateOcto': 'Корпоративные карты не принимаются.',

  // ── Company id: БИН/ИИН, not ИНН ────────────────────────────────────────
  // ⚠️ `invoice.buyerInn` is deliberately NOT here: it is the operator's own
  // Uzbek ИНН (a legal requisite, unchanged on KZ). `footer.inn` labels the same
  // number, but a bare «ИНН: 306377222» under a Kazakh catalogue read like a
  // malformed БИН (audit 2026-10-17), so it says whose ИНН it is.
  'footer.inn': 'ИНН (Узбекистан)',
  'support.companyData.topic': 'Тема: данные компании, БИН/ИИН {{tin}}',
  'support.companyData.prefill': 'Данные компании БИН/ИИН {{tin}}. ',
  'results.wall.line': 'Контакты, БИН/ИИН и суммы победителей — в Pro',
  'buyer.tin': 'БИН',
  // Owner, 2026-10-15: kaztender.com/supplier/<БИН> printed «ИНН 160240031454».
  'org.tinNumber': 'БИН {{number}}',
  'buyer.seoTitle': '«{{name}}» — БИН {{tin}}: закупки и поставщики',
  'buyer.seoDescription':
    '{{name}}, БИН {{tin}} — {{awards}} контрактов на {{value}}, {{suppliers}} поставщиков, {{active}} активных тендеров. Категории и суммы — {{brand}}.',
  'supplier.seoTitle': '«{{name}}» — БИН/ИИН {{tin}}: госзакупки, контракты, победы',
  'supplier.seoDescription':
    '{{name}}, БИН/ИИН {{tin}} — {{contracts}} госконтрактов на {{value}}, {{buyers}} заказчиков, {{period}}. Категории и регионы поставок — {{brand}}.',
  'invoiceFlow.tinLabel': 'БИН/ИИН вашей организации',
  'invoiceFlow.errIssue': 'Не удалось выставить счёт. Проверьте БИН/ИИН и попробуйте ещё раз.',
  'companies.search': 'Название или БИН/ИИН',
  'tender.bidPackage.noCompany':
    'БИН/ИИН в профиле не указан, поэтому проверить требования по вашей компании мы не смогли — все пункты нужно проверить самостоятельно.',
  'freeUnlock.needTin': 'Добавьте БИН/ИИН компании',
  'company.card.notFound':
    'Мы не нашли компанию с таким БИН/ИИН в реестре. Проверьте номер — или оставьте как есть, если вы ИП: реестр компаний их не возвращает.',
  'company.savedTitle': 'БИН/ИИН сохранён',
  'settings.summary.noCompany': 'БИН/ИИН не указан',
  'company.tinLabel': 'БИН/ИИН',
  'company.saved': 'БИН/ИИН сохранён.',
  'company.error.invalidTin': 'Введите {{digits}} цифр БИН или ИИН',
  // Все 12 цифр есть, контрольная не сошлась — опечатка, а не «введите 12 цифр» (Redmi, 22.10).
  'company.error.checksum': 'Номер не прошёл проверку — сверьте цифры БИН/ИИН',
  'company.error.changeLimit': 'БИН/ИИН сегодня меняли слишком часто. Попробуйте завтра.',
  // Шаг компании — одно поле «название или БИН/ИИН» (владелец, 19.10: «примерно
  // как сейчас в UzTender»). ru.ts подставляет {{tin}} = «БИН/ИИН» и сам, но
  // формулировка владельца на KZ закреплена здесь явно.
  'onboarding.inn.title': 'Название или БИН/ИИН вашей организации?',
  'company.title': 'Название или БИН/ИИН вашей организации?',
  'company.lookup.label': 'Название или БИН/ИИН вашей организации',
  'company.lookup.placeholder': 'Название или {{digits}} цифр БИН/ИИН',
  'company.lookup.emptyNoSkip': 'Не нашли по названию — введите БИН/ИИН',
  'company.lookup.tinValue': 'БИН/ИИН {{value}}',
  'claim.card.needsTin': 'Сначала укажите БИН/ИИН компании в разделе выше.',
  'claim.conflict.title': 'Этот БИН/ИИН уже привязан к другому аккаунту',

  // ── Classifier: ЕНС ТРУ, not ЕНКТ ───────────────────────────────────────
  'results.card.inputLabel': 'Слова, категории и коды ЕНС ТРУ',
  'results.card.ikpu': 'ЕНС ТРУ',
  'results.change.ikpu': 'коды ЕНС ТРУ',
  'savedSearches.instant.no.broad':
    'Только для поисков с уточнением (слово, ЕНС ТРУ, регион, ниша…)',
  'saved.edit.errorSubject':
    'Оставьте хотя бы одно: категорию, ЕНС ТРУ, нишу, заказчика или слово.',
  'saved.categoryAdd': 'Добавить категорию, ЕНС ТРУ или нишу',
  'saved.ikpuLabel': 'Коды ЕНС ТРУ подписки',
  'saved.ikpuRemove': 'Убрать код ЕНС ТРУ',
  'similarWinners.hint':
    'Компании, выигравшие закупки с тем же кодом ЕНС ТРУ за последние 24 месяца. Если по точному коду победителей меньше трёх — по классу кода (первые 6 цифр). Доля — доля побед.',
  'similarWinners.basisExact': 'Код ЕНС ТРУ {{code}}: {{awards}}, {{winners}} за 24 месяца',
  'similarWinners.basisClass':
    'Класс ЕНС ТРУ {{code}} — по точному коду победителей мало: {{awards}}, {{winners}} за 24 месяца',

  // ── ИИ-онбординг (on Kazakhstan only, 2026-10-15) ────────────────────────
  'aiOnboarding.row.subject': 'Категории, ЕНС ТРУ и ниши',
  'aiOnboarding.row.ikpu': 'Коды ЕНС ТРУ',
  // ru.ts says «Слова ru + uz» — Uzbekistan expands every word into both
  // scripts; Kazakhstan keeps the words as written (Russian, maybe Kazakh).
  'aiOnboarding.row.keywords': 'Слова',
  // «от 5 млн» filtered out most of the catalogue: the median live KZ lot is
  // 65 520 ₸ (audit 2026-10-17), so the example floor is 500 тыс.
  'aiOnboarding.fill.placeholder': 'нужны насосы и электродвигатели, Алматы и область, от 500 тыс.',

  // ── Misc ────────────────────────────────────────────────────────────────
  'register.phonePlaceholder': '+7 701 234 56 78',
  'register.phoneInvalid': 'Введите корректный номер, например +7 701 234 56 78',
  // The scale itself is the owner's Uzbek one (2026-09-29, in so'm). Only the
  // currency WORD follows the deployment here; the thresholds for Kazakhstan are
  // an open commercial decision.
  'tender.bidWaitlistForm.tier1Range': 'до 100 млн {{currency}}',
  // Example amounts: ru.ts writes them on the so'm scale (500 млн / 1 млрд /
  // 2,4 млрд), which read as tenge here are ~25× too large. A tenge is ~25 so'm.
  // The saved-search range went down again 2026-10-18 (20/40 млн → 0,5/5 млн):
  // the median live KZ lot is 65 520 ₸, so 20 млн as a floor hid nearly all.
  'saved.minBudgetPlaceholder': '500 000',
  'saved.maxBudgetPlaceholder': '5 000 000',
  'onboarding.sample.meta': '💰 96 000 000 {{currency}} · ⏰ осталось 12 дней',
  // Owner, 2026-10-21: «Оплатить», not «Подписаться». 19–21.10 thirteen people pressed
  // «Подписаться на Pro» and twelve of them left before the card sheet even opened —
  // the word promised a commitment where the sheet asks for one month's card payment.
  'plans.subscribeWithCard': 'Оплатить {{name}}',
  // Owner's rule for Kazakhstan (19.10): never sell «контакты» — on goszakup they are open.
  // Pro is sold as documents + the original lot, winners' prices and contract history,
  // analytics, every platform in one place. Found on 21.10 still saying «контакты» on
  // /plans (lead, feature line, SEO title) and in the menu promo.
  'plans.pageLead':
    'Читать и разбирать тендеры можно бесплатно. Тариф открывает документы и оригинал лота, цены победителей и историю контрактов, аналитику по заказчикам — то, что нужно, чтобы участвовать.',
  'plans.title': 'Все тендеры Казахстана, документы и аналитика',
  'plans.seoTitle': 'Тарифы и цены — Pro на KazTender',
  'plans.feature.contactsAll': 'Оригинал тендера на площадке — в один клик',
  'shell.upgrade.desc': 'Документы и оригиналы лотов, цены победителей, аналитика',
  'settings.summary.planFreeHint': 'Документы и оригиналы лотов закрыты',
};
